<script setup lang="ts">
import { ref, watch } from 'vue'
import { VueDraggable } from 'vue-draggable-plus'
import { Bars3Icon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import { CLIENT360_SECTION_LABELS, CLIENT360_ALWAYS_VISIBLE } from './client360Sections'

// Liste éditable des sections : réordonnancement (drag) + masquage/affichage (œil).
const props = defineProps<{
  order: string[]
  hidden: string[]
}>()

const emit = defineEmits<{ (e: 'change', value: { order: string[]; hidden: string[] }): void }>()

interface Row {
  id: string
  label: string
  hidden: boolean
}

function build(): Row[] {
  return props.order.map((id) => ({
    id,
    label: CLIENT360_SECTION_LABELS[id] || id,
    hidden: props.hidden.includes(id),
  }))
}

const list = ref<Row[]>(build())

watch(
  () => [props.order, props.hidden],
  () => {
    list.value = build()
  },
  { deep: true },
)

function emitChange() {
  emit('change', {
    order: list.value.map((r) => r.id),
    hidden: list.value.filter((r) => r.hidden).map((r) => r.id),
  })
}

function toggle(row: Row) {
  if (CLIENT360_ALWAYS_VISIBLE.includes(row.id)) return
  row.hidden = !row.hidden
  emitChange()
}
</script>

<template>
  <VueDraggable v-model="list" :animation="150" handle=".drag-handle" class="space-y-1.5" @end="emitChange">
    <div
      v-for="row in list"
      :key="row.id"
      class="flex items-center gap-2 px-3 py-2 rounded-lg bg-base-200/60 border border-base-200"
      :class="row.hidden ? 'opacity-50' : ''"
    >
      <Bars3Icon class="w-4 h-4 text-base-content/40 cursor-grab drag-handle shrink-0" />
      <span class="text-sm flex-1">{{ row.label }}</span>
      <button
        type="button"
        class="p-1 rounded hover:bg-base-300/60 transition-colors"
        :class="CLIENT360_ALWAYS_VISIBLE.includes(row.id) ? 'opacity-30 cursor-not-allowed' : ''"
        :title="row.hidden ? 'Afficher' : 'Masquer'"
        :disabled="CLIENT360_ALWAYS_VISIBLE.includes(row.id)"
        @click="toggle(row)"
      >
        <EyeSlashIcon v-if="row.hidden" class="w-4 h-4 text-base-content/50" />
        <EyeIcon v-else class="w-4 h-4 text-primary" />
      </button>
    </div>
  </VueDraggable>
</template>
